import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { OnDragEndResponder } from '@hello-pangea/dnd';
import _ from 'lodash';

import ShopTabsTable from './Table/ShopTabsTable';
import { Loading, Modal, NewRow, Section } from '../shared';
import { LanguageType } from '@lifeserver/shared';

import { useTabs } from '../hooks/tabs';

import { handleEnvAuth } from '../utils/envaccess';
import {
    fetchDataFromPath,
    postDataForPath,
    putDataForPath,
} from '../utils/envcopy';
import { PACKS_BASE_PATH, SHOP_TABS_BASE_PATH } from '../utils/pathsConstants';
import { copyMedias } from '../utils/upload';
import { handleError, moveListItem } from '../utils/utils';

import { RouteTo } from '../routes';

import { Environment } from '../types/environmentTypes';
import { Media } from '../types/mediaTypes';
import { Tab } from '../types/tabTypes';

type ShopTabsProps = {
    language: LanguageType;
};

const ShopTabs = function (props: ShopTabsProps) {
    const [isCopying, setIsCopying] = useState(false);
    const [isCopyingMedia, setIsCopyingMedia] = useState(false);
    const [copyMediaProgress, setCopyMediaProgress] = useState({
        current: 0,
        max: 0,
    });
    const navigate = useNavigate();

    const {
        data: tabs = [],
        isPending,
        isLoading,
        setTabActive,
        deleteTab,
        updateTabsOrder,
    } = useTabs();

    const handleDragEnd: OnDragEndResponder = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        const items = moveListItem(
            tabs,
            result.source.index,
            result.destination.index,
        );
        updateTabsOrder.mutate({ tabIds: items.map((tab) => tab.id) });
    };

    const handleTabClick = (tabId: Tab['id']) => {
        navigate(RouteTo.TabEdit(String(tabId)));
    };

    const handleDeleteTab = async (
        e: MouseEvent<HTMLButtonElement>,
        tabId: Tab['id'],
    ) => {
        e.stopPropagation();

        const isConfirmed = await Modal.confirm({
            heading: 'Delete tab',
            text: 'Really delete this tab?',
            okLabel: 'Yes do it!',
        });
        if (!isConfirmed) return;

        try {
            await deleteTab.mutateAsync({ tabId });
        } catch (error) {
            handleError('Error deleting tab', error);
        }
    };

    const handleToggleActivateStatus = async (
        e: MouseEvent<HTMLButtonElement>,
        tab: Tab,
    ) => {
        e.stopPropagation();

        const isConfirmed = await Modal.confirm({
            heading: `${tab.active ? 'Deactivate' : 'Activate'} tab`,
            text: `Really ${
                tab.active ? 'deactivate' : 'activate'
            } tab with name "${tab.name}"?`,
            okLabel: 'Yes do it!',
        });
        if (!isConfirmed) return;

        try {
            await setTabActive.mutateAsync({
                tabId: tab.id,
                activeState: !tab.active,
            });
        } catch (error: unknown) {
            handleError('Error activating / deactivating tab', error);
        }
    };

    const onMediaUploadCompleteCallback = () => {
        setCopyMediaProgress((prevCopyMediaProgress) => ({
            ...prevCopyMediaProgress,
            current: _.clamp(
                prevCopyMediaProgress.current + 1,
                prevCopyMediaProgress.max,
            ),
        }));
    };

    const prepareTabContentToCopy = async (tab: Tab, env: Environment) => {
        if (!_.isEmpty(tab.packIds)) {
            const foundPacks = [];

            for (const packId of tab.packIds) {
                const fetchedData = await fetchDataFromPath(
                    `${env.basepath}/${PACKS_BASE_PATH}/v2/${packId}`,
                );
                const fetchedPack = fetchedData.pack;
                if (!_.isEmpty(fetchedPack)) {
                    foundPacks.push(fetchedPack.packId);
                }
            }
            const isConfirmed = await Modal.confirm({
                heading: 'Packs found',
                text: `${foundPacks.length} out of ${tab.packIds.length} packs were found in ${env.name}, do you still want to continue?`,
                okLabel: 'Continue',
            });
            if (!isConfirmed) return false;

            tab.packIds = foundPacks;
        }

        if (tab.medias.length) {
            const handleMedia = await Modal.select({
                items: [
                    {
                        label: 'Copy media',
                        value: 'yes',
                        disabled: false,
                    },
                    {
                        label: 'Skip media',
                        value: 'no',
                        disabled: false,
                    },
                ],
                heading: 'Also copy media?',
                okLabel: 'Continue',
                hint: `Media will be copied to ${env.name}`,
                inputName: 'media',
            });
            if (!handleMedia) return false;

            if (handleMedia === 'yes') {
                setIsCopyingMedia(true);
                const { medias } = tab;

                const copyMediaProgress = {
                    current: 1,
                    max: medias.length,
                };
                setCopyMediaProgress(copyMediaProgress);

                const newMedias = await copyMedias(
                    medias as Media[],
                    tab.name.toLowerCase(),
                    env,
                    onMediaUploadCompleteCallback,
                );
                tab.mediaIds = newMedias.map((m) => m.mediaId);

                setIsCopyingMedia(false);
            }
        }

        // Set new tab to inactive and at the top of the tabs list
        tab.active = false;
        tab.priority = 0;

        return tab;
    };

    const handleCopyTab = async (
        e: MouseEvent<HTMLButtonElement>,
        item: Tab,
    ) => {
        e.stopPropagation();

        setIsCopying(true);

        try {
            const env = await handleEnvAuth();
            if (!env) {
                return;
            }

            // Get and compare tabs
            const currentTab = await fetchDataFromPath(
                `/api/${SHOP_TABS_BASE_PATH}/${item.id}`,
            );
            const refTab = await fetchDataFromPath(
                `${env.basepath}/${SHOP_TABS_BASE_PATH}/name/${item.name}`,
            );

            if (_.isEmpty(refTab)) {
                // No tab in target environment, show confirm modal
                const isConfirmed = await Modal.confirm({
                    heading: 'Copy tab',
                    text: `No tab with name '${item.name}' found in ${env.name}, do you want to create new tab?`,
                    okLabel: 'Yes do it!',
                });
                if (!isConfirmed) {
                    return;
                }
            } else {
                // Tab was found, show compare modal
                const isCompareConfirmed = await Modal.compare({
                    heading: 'Compare tabs',
                    text: 'The following differences were found',
                    okLabel: 'Copy',
                    object1: refTab,
                    object2: currentTab,
                });
                if (!isCompareConfirmed) {
                    return;
                }
            }

            // Prepare tab content before copy
            const tab = await prepareTabContentToCopy(currentTab, env);
            if (!tab) {
                return;
            }

            // Finally, copy tab to target environment
            const hasCopiedSuccessfully = _.isEmpty(refTab)
                ? await postDataForPath(
                      tab,
                      `${env.basepath}/${SHOP_TABS_BASE_PATH}`,
                  )
                : await putDataForPath(
                      tab,
                      `${env.basepath}/${SHOP_TABS_BASE_PATH}/${refTab.id}`,
                  );

            const modalHeading = hasCopiedSuccessfully
                ? 'Copying done!'
                : 'On no!';
            const modalText = hasCopiedSuccessfully
                ? `Woho, the tab with name '${tab.name}' was successfully stored in ${env.name}!`
                : `Something went wrong when trying to copy tab with name '${tab.name}' to ${env.name}!`;

            await Modal.alert({
                heading: modalHeading,
                text: modalText,
            });
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <Section>
            <div style={{ display: 'inline-block' }}>
                <h1>Shop tabs</h1>
                <button
                    className="new withHeading"
                    onClick={() => navigate(RouteTo.TabNew())}
                >
                    Add new
                </button>
            </div>
            <NewRow />
            {(isCopying || isCopyingMedia) && (
                <Loading
                    text={
                        isCopyingMedia
                            ? `Uploading media file ${copyMediaProgress.current} of ${copyMediaProgress.max}...`
                            : 'Copying tab'
                    }
                />
            )}
            {deleteTab.isPending && <Loading text={'Deleting tab...'} />}
            {setTabActive.isPending && (
                <Loading text={'Updating tab active state...'} />
            )}
            {isLoading && <Loading text={'Fetching tabs...'} />}
            {!isPending && (
                <Section>
                    <ShopTabsTable
                        tabs={tabs}
                        onTabClick={handleTabClick}
                        onDragEnd={handleDragEnd}
                        onCopyTabClick={handleCopyTab}
                        onDeleteTabClick={handleDeleteTab}
                        onToggleActiveClick={handleToggleActivateStatus}
                        {...props}
                    />
                </Section>
            )}
        </Section>
    );
};

export default ShopTabs;
